import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Camera, CheckCircle2, CloudOff, ImagePlus, Loader2, Printer, RefreshCw, Send, Trash2, X } from "lucide-react";
import BlueCardPrint from "@/components/BlueCardPrint";
import { toast } from "sonner";
import usePoll from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { api, compressImage, dt, errMsg, money, uid, uploadDataUrl } from "@/lib/api";
import { loadDrafts, removeDraft, saveDraft } from "@/lib/drafts";
import { Empty, ErrorBox, Field, Loading, Modal, Sel } from "@/components/common";
import { SelectionBadge, SelectionInfo } from "@/components/Selection";

const MAX = 6;

function PhotoPicker({ photos, setPhotos }) {
  const add = async (e) => {
    const files = [...e.target.files].slice(0, MAX - photos.length);
    e.target.value = "";
    try { const out = await Promise.all(files.map((f) => compressImage(f))); setPhotos((p) => [...p, ...out].slice(0, MAX)); } catch (ex) { toast.error(ex.message); }
  };
  const btn = "k-btn-outline flex-1 h-14 cursor-pointer";
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <label className={btn} data-testid="photo-camera-btn"><Camera className="w-5 h-5" /> الكاميرا<input type="file" accept="image/*" capture="environment" className="hidden" onChange={add} data-testid="photo-camera-input" /></label>
        <label className={btn} data-testid="photo-gallery-btn"><ImagePlus className="w-5 h-5" /> من الجهاز<input type="file" accept="image/*" multiple className="hidden" onChange={add} data-testid="photo-gallery-input" /></label>
      </div>
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="photo-previews">
          {photos.map((p, i) => (
            <div key={i} className="relative"><img src={p} alt="" className="w-20 h-20 rounded-xl object-cover border" />
              <button type="button" className="absolute -top-2 -left-2 bg-rose-600 text-white rounded-full p-1" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} data-testid={`remove-photo-${i}`}><X className="w-3 h-3" /></button></div>
          ))}
        </div>
      )}
      <div className="text-xs text-slate-500">{photos.length}/{MAX} صور – تُحفظ في التخزين الدائم مع سجل الكبش</div>
    </div>
  );
}

async function send(payload, photos) {
  const { data } = await api.post("/selections", payload);
  const already = data.photos?.length || 0;
  let failed = [];
  for (const p of photos.slice(already)) {
    try { await uploadDataUrl(data.id, p); } catch { failed.push(p); }
  }
  return { sel: data, failed };
}

const selCard = (s) => ({ ear_tag: s.ear_tag, price: s.price, section: `${s.section_code} – ${s.section_name}`, worker_name: s.worker_name, selected_at: s.selected_at, number: s.number });

function Drafts({ drafts, onRetry, onDelete, onPrint, busyKey }) {
  if (!drafts.length) return null;
  return (
    <section className="k-card p-4 space-y-3 border-rose-300" data-testid="drafts-section">
      <h2 className="font-bold flex items-center gap-2 text-rose-800"><CloudOff className="w-5 h-5" /> مسودات على هذا الهاتف ({drafts.length})</h2>
      {drafts.map((d) => (
        <div key={d.client_key} className="rounded-xl border border-dashed border-rose-300 bg-rose-50/60 p-3 flex items-center gap-3" data-testid={`draft-${d.payload.ear_tag}`}>
          <div className="flex-1">
            <div className="font-black num text-lg">{d.payload.ear_tag} <span className="text-sm font-bold">{money(d.payload.price)}</span></div>
            <div className="text-xs font-bold text-rose-700">غير متزامنة مع الخادم – لا يراها المكتب</div>
            <div className="text-xs text-slate-500">{dt(d.saved_at)} • {d.photos.length} صور{d.photos_dropped ? " (لم تُحفظ الصور لامتلاء الذاكرة)" : ""}</div>
          </div>
          <button className="k-btn-primary k-btn-sm" disabled={busyKey === d.client_key} onClick={() => onRetry(d)} data-testid={`draft-retry-${d.payload.ear_tag}`}>{busyKey === d.client_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} إعادة الإرسال</button>
          <button className="k-btn-outline k-btn-sm" onClick={() => onPrint(d)} data-testid={`draft-print-${d.payload.ear_tag}`}><Printer className="w-4 h-4" /></button>
          <button className="k-btn-outline k-btn-sm text-rose-700" onClick={() => onDelete(d)} data-testid={`draft-delete-${d.payload.ear_tag}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </section>
  );
}

function MySelections({ reloadKey, onPrint }) {
  const { data, error, reload } = usePoll("/selections", null, 20000);
  const [open, setOpen] = useState(null);
  useEffect(() => { reload(); }, [reloadKey, reload]);
  return (
    <section className="k-card p-4 space-y-2" data-testid="my-selections">
      <h2 className="font-bold">اختياراتي المسجلة</h2>
      <ErrorBox text={error} />
      {!data ? <Loading /> : !data.length ? <Empty text="لا توجد اختيارات بعد" /> : data.slice(0, 50).map((s) => (
        <button key={s.id} className="w-full text-right rounded-xl border p-3 flex items-center gap-3 hover:bg-[#F6F1E7]" onClick={() => setOpen(s)} data-testid={`my-selection-${s.ear_tag}`}>
          <div className="flex-1"><div className="font-black num text-lg">{s.ear_tag}</div><div className="text-xs text-slate-500">{s.section_name} • {dt(s.selected_at)}</div></div>
          <SelectionBadge s={s.status} testid={`my-selection-status-${s.ear_tag}`} />
        </button>
      ))}
      <Modal open={!!open} onOpenChange={(o) => !o && setOpen(null)} title={`الكبش ${open?.ear_tag}`}
        footer={open && <button className="k-btn-outline" onClick={() => onPrint(selCard(open))} data-testid="my-selection-print-btn"><Printer className="w-4 h-4" /> طباعة البطاقة الزرقاء</button>}>{open && <SelectionInfo sel={open} />}</Modal>
    </section>
  );
}

export default function PenWorker() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [f, setF] = useState({ ear_tag: "", section_id: "", price: "", worker_id: user.id, notes: "" });
  const [photos, setPhotos] = useState([]);
  const [key, setKey] = useState(uid());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [offline, setOffline] = useState(false);
  const [existing, setExisting] = useState(null);
  const [done, setDone] = useState(null);
  const [drafts, setDrafts] = useState(() => loadDrafts(user.id));
  const [busyKey, setBusyKey] = useState(null);
  const [now, setNow] = useState(new Date().toISOString());
  const [card, setCard] = useState(null);
  const endPrint = useCallback(() => setCard(null), []);
  const draftCard = (d) => {
    const s = meta?.sections.find((x) => x.id === d.payload.section_id);
    const w = d.payload.worker_id === user.id ? user.full_name : meta?.workers.find((x) => x.id === d.payload.worker_id)?.full_name;
    setCard({ ear_tag: d.payload.ear_tag, price: d.payload.price, section: s ? `${s.code} – ${s.name}` : "—", worker_name: w || user.full_name, selected_at: d.saved_at, draft: true });
  };

  useEffect(() => { api.get("/selections/meta").then((r) => { setMeta(r.data); if (r.data.sections.length === 1) pick(r.data.sections[0].id, r.data.sections); }).catch((e) => setErr(errMsg(e))); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => setNow(new Date().toISOString()), 30000); return () => clearInterval(t); }, []);
  function pick(id, secs = meta?.sections) { const s = secs?.find((x) => x.id === id); setF((o) => ({ ...o, section_id: id, price: s ? String(s.default_price) : o.price })); }
  const payload = () => ({ client_key: key, ear_tag: f.ear_tag.trim().toUpperCase().replace(/\s+/g, ""), section_id: f.section_id, price: Number(f.price), worker_id: f.worker_id || null, notes: f.notes || null });
  const reset = () => { setF((o) => ({ ...o, ear_tag: "", notes: "", price: String(meta?.sections.find((s) => s.id === o.section_id)?.default_price || "") })); setPhotos([]); setKey(uid()); setDone(null); setOffline(false); setExisting(null); setErr(""); };

  const handleErr = (ex) => {
    if (!ex.response) { setOffline(true); return; }
    if (ex.response.status === 409 && ex.response.data?.detail?.existing) setExisting(ex.response.data.detail.existing);
    setErr(errMsg(ex));
  };
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setOffline(false); setExisting(null);
    if (!f.ear_tag.trim()) return setErr("رقم الكبش مطلوب");
    if (!f.section_id) return setErr("يرجى اختيار القسم");
    if (!Number(f.price)) return setErr("يرجى إدخال السعر المتفق عليه");
    setBusy(true);
    try { setDone(await send(payload(), photos)); } catch (ex) { handleErr(ex); } finally { setBusy(false); }
  };
  const keepDraft = () => {
    const r = saveDraft(user.id, { client_key: key, payload: payload(), photos, saved_at: new Date().toISOString() });
    if (r === "fail") return toast.error("تعذر الحفظ على الهاتف – استعمل البطاقة الزرقاء الورقية");
    const pl = payload();
    toast.warning(r === "no_photos" ? "حُفظت المسودة بدون صور (ذاكرة الهاتف ممتلئة)" : "حُفظت المسودة على الهاتف – غير متزامنة مع الخادم",
      { action: { label: "طباعة البطاقة", onClick: () => draftCard({ payload: pl, saved_at: new Date().toISOString() }) }, duration: 8000 });
    setDrafts(loadDrafts(user.id)); reset();
  };
  const retry = async (d) => {
    setBusyKey(d.client_key);
    try {
      const r = await send({ ...d.payload, draft_saved_at: d.saved_at }, d.photos);
      removeDraft(user.id, d.client_key); setDrafts(loadDrafts(user.id)); setDone(r);
      toast.success(`تمت مزامنة الكبش ${r.sel.ear_tag} مع الخادم`);
    } catch (ex) {
      if (ex.response?.status === 409) { removeDraft(user.id, d.client_key); setDrafts(loadDrafts(user.id)); handleErr(ex); }
      else toast.error(ex.response ? errMsg(ex) : "لا يزال الاتصال متعذرًا – لم تُرسل المسودة");
    } finally { setBusyKey(null); }
  };
  const retryPhotos = async () => {
    setBusy(true); const failed = [];
    for (const p of done.failed) { try { await uploadDataUrl(done.sel.id, p); } catch { failed.push(p); } }
    setDone({ ...done, failed }); setBusy(false);
  };

  if (!meta) return err ? <ErrorBox text={err} /> : <Loading />;
  if (!meta.sections.length) return <div className="k-card p-8 text-center font-bold text-rose-700" data-testid="no-sections">لا توجد أقسام مسندة إليك. يرجى مراجعة المدير لتحديد الأقسام التي تعمل بها.</div>;
  return (
    <div className="max-w-xl mx-auto space-y-4">
      <div><h1 className="text-2xl font-extrabold text-[#1A4D2E]" data-testid="page-title">تسجيل اختيار كبش</h1><p className="text-sm text-slate-500">عند اختيار الزبون لكبش في الحضيرة</p></div>
      {card && <BlueCardPrint card={card} onDone={endPrint} />}
      <Drafts drafts={drafts} onPrint={draftCard} onRetry={retry} onDelete={(d) => { removeDraft(user.id, d.client_key); setDrafts(loadDrafts(user.id)); }} busyKey={busyKey} />
      {done ? (
        <section className="k-card p-6 text-center space-y-3 border-emerald-400 bg-emerald-50" data-testid="selection-success">
          <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto" />
          <div className="text-xl font-extrabold text-emerald-800">{done.sel.duplicate_request ? "هذا السجل محفوظ مسبقًا في الخادم" : "تم التسجيل بنجاح في قاعدة البيانات المركزية"}</div>
          <div className="text-4xl font-black num" data-testid="success-ear-tag">{done.sel.ear_tag}</div>
          <div className="text-sm">رقم السجل <b className="num" data-testid="success-number">{done.sel.number}</b> • {money(done.sel.price)} • بانتظار إتمام البيع في المكتب</div>
          {done.failed.length > 0 && <div className="rounded-xl bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900" data-testid="photo-upload-failed">لم تُرفع {done.failed.length} صورة. السجل محفوظ.
            <button className="k-btn-outline k-btn-sm mr-2" disabled={busy} onClick={retryPhotos} data-testid="retry-photos-btn">إعادة رفع الصور</button></div>}
          <button className="k-btn-accent w-full h-14 text-lg" onClick={() => setCard(selCard(done.sel))} data-testid="print-blue-card-btn"><Printer className="w-5 h-5" /> طباعة البطاقة الزرقاء (80 مم)</button>
          <button className="k-btn-primary w-full h-14 text-lg" onClick={reset} data-testid="new-selection-btn">تسجيل كبش آخر</button>
        </section>
      ) : (
        <form onSubmit={submit} className="k-card p-4 space-y-4" data-testid="selection-form">
          <Field label="رقم الكبش المثبت عليه *"><input className="k-input text-3xl font-black num text-center tracking-widest h-16" inputMode="text" autoComplete="off" value={f.ear_tag} onChange={(e) => setF({ ...f, ear_tag: e.target.value })} data-testid="sel-ear-tag-input" /></Field>
          <Field label="القسم *"><Sel value={f.section_id} onChange={(v) => pick(v)} testid="sel-section-select"><option value="">— اختر القسم —</option>{meta.sections.map((s) => <option key={s.id} value={s.id}>{s.code} – {s.name} ({money(s.default_price)})</option>)}</Sel></Field>
          <Field label="السعر المتفق عليه (دج) *"><input className="k-input num text-right text-2xl h-14" inputMode="numeric" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value.replace(/\D/g, "") })} data-testid="sel-price-input" /></Field>
          <Field label="العامل المشرف على الاختيار"><Sel value={f.worker_id} onChange={(v) => setF({ ...f, worker_id: v })} testid="sel-worker-select">
            <option value={user.id}>{user.full_name} (أنا)</option>{meta.workers.filter((w) => w.id !== user.id).map((w) => <option key={w.id} value={w.id}>{w.full_name}</option>)}</Sel></Field>
          <div className="text-sm text-slate-600" data-testid="sel-time">تاريخ ووقت الاختيار: <b>{dt(now)}</b> (يُسجل تلقائيًا عند الإرسال)</div>
          <PhotoPicker photos={photos} setPhotos={setPhotos} />
          <Field label="ملاحظات"><textarea className="k-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="sel-notes-input" /></Field>
          {offline && (
            <div className="rounded-xl border-2 border-rose-400 bg-rose-50 p-4 space-y-2" data-testid="offline-error">
              <div className="font-extrabold text-rose-800 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> لم تُرسل البيانات إلى الخادم</div>
              <p className="text-sm text-rose-900">تعذر الاتصال. السجل غير محفوظ في قاعدة البيانات المركزية ولن يراه المكتب. احفظه كمسودة وأعد إرساله عند عودة الاتصال، أو استعمل البطاقة الزرقاء الورقية.</p>
              <button type="button" className="k-btn-accent w-full" onClick={keepDraft} data-testid="save-draft-btn"><CloudOff className="w-5 h-5" /> حفظ كمسودة على الهاتف</button>
            </div>
          )}
          {existing && <div className="rounded-xl border border-amber-300 bg-amber-50 p-3" data-testid="existing-selection"><div className="font-bold mb-2">السجل الموجود:</div><SelectionInfo sel={existing} /></div>}
          <ErrorBox text={err} />
          <button className="k-btn-primary w-full h-16 text-xl" disabled={busy} data-testid="sel-submit-btn">{busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />} إرسال إلى المكتب</button>
        </form>
      )}
      <MySelections reloadKey={done?.sel?.id} onPrint={setCard} />
    </div>
  );
}
